import React, { useState } from "react";
import { generateUserID } from "../utils/uuid";

const UserRegistration = ({ onComplete, isWidget = false }) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    } else if (formData.name.trim().length < 2) {
      newErrors.name = "Name must be at least 2 characters";
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = "Please enter a valid email";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const userId = generateUserID();
      let userData = {
        userId,
        name: formData.name.trim(),
        email: formData.email.trim().toLowerCase(),
        createdAt: new Date().toISOString(),
        source: "web-chat",
      };

      // Send to API
      const apiUrl = process.env.REACT_APP_USER_REGISTRATION_URL || "/api";
      const response = await fetch(`${apiUrl}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(userData),
      });

      if (!response.ok) {
        throw new Error("Registration failed");
      }

      let data = await response.json();
      let returnedUserId = data?.userId;
      userData = { ...userData, userId: returnedUserId };

      // Save to localStorage
      localStorage.setItem("bsa_user_id", returnedUserId);
      localStorage.setItem(
        "bsa_user_data",
        JSON.stringify({
          userId: returnedUserId,
          name: userData.name,
          email: userData.email,
          registeredAt: userData.createdAt,
        })
      );

      // Call onComplete with user data
      onComplete(userData);
    } catch (error) {
      console.error("Registration error:", error);
      setErrors({ submit: "Registration failed. Please try again." });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  return (
    <div className={`${isWidget ? "h-full" : ""} flex flex-col`}>
      <div
        className={`flex-1 overflow-y-auto ${
          isWidget ? "px-3 py-4" : "px-4 py-6"
        }`}
      >
        <div className={`${isWidget ? "" : "max-w-md mx-auto"}`}>
          {/* Header */}
          <div className={`text-center ${isWidget ? "mb-4" : "mb-6"}`}>
            <div
              className={`${
                isWidget ? "w-12 h-12" : "w-16 h-16"
              } mx-auto mb-3 bg-gradient-to-br from-blue-400 to-purple-600 
                            rounded-2xl flex items-center justify-center shadow-lg shadow-purple-500/30`}
            >
              <span
                className={`text-white ${
                  isWidget ? "text-xl" : "text-2xl"
                } font-bold`}
              >
                B
              </span>
            </div>
            <h2
              className={`${
                isWidget ? "text-lg" : "text-xl sm:text-2xl"
              } font-bold text-white mb-1`}
            >
              Welcome to BSA Assist!
            </h2>
            <p className={`text-gray-400 ${isWidget ? "text-xs" : "text-sm"}`}>
              {isWidget
                ? "Quick registration to get started"
                : "Let's get to know you better"}
            </p>
          </div>

          {/* Form */}
          <form
            onSubmit={handleSubmit}
            className={`${isWidget ? "space-y-3" : "space-y-4"}`}
          >
            {/* Name Field */}
            <div>
              <label
                htmlFor="name"
                className={`block ${
                  isWidget ? "text-xs" : "text-sm"
                } font-medium text-gray-300 mb-1.5`}
              >
                Your Name
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className={`w-full ${
                  isWidget ? "p-2.5" : "p-3"
                } rounded-xl bg-gray-800/50 text-white placeholder-gray-500 
                           focus:outline-none border transition-all duration-300 ${
                             isWidget ? "text-sm" : ""
                           }
                           ${
                             errors.name
                               ? "border-red-500/50 focus:border-red-400"
                               : "border-blue-500/30 focus:border-blue-400"
                           } 
                           focus:ring-2 ${
                             errors.name
                               ? "focus:ring-red-400/20"
                               : "focus:ring-blue-400/20"
                           }`}
                placeholder="John Doe"
                autoFocus
                disabled={isSubmitting}
              />
              {errors.name && (
                <p
                  className={`mt-1 ${
                    isWidget ? "text-xs" : "text-xs"
                  } text-red-400`}
                >
                  {errors.name}
                </p>
              )}
            </div>

            {/* Email Field */}
            <div>
              <label
                htmlFor="email"
                className={`block ${
                  isWidget ? "text-xs" : "text-sm"
                } font-medium text-gray-300 mb-1.5`}
              >
                Email Address
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className={`w-full ${
                  isWidget ? "p-2.5" : "p-3"
                } rounded-xl bg-gray-800/50 text-white placeholder-gray-500 
                           focus:outline-none border transition-all duration-300 ${
                             isWidget ? "text-sm" : ""
                           }
                           ${
                             errors.email
                               ? "border-red-500/50 focus:border-red-400"
                               : "border-blue-500/30 focus:border-blue-400"
                           } 
                           focus:ring-2 ${
                             errors.email
                               ? "focus:ring-red-400/20"
                               : "focus:ring-blue-400/20"
                           }`}
                placeholder="john@example.com"
                disabled={isSubmitting}
              />
              {errors.email && (
                <p
                  className={`mt-1 ${
                    isWidget ? "text-xs" : "text-xs"
                  } text-red-400`}
                >
                  {errors.email}
                </p>
              )}
            </div>

            {/* Submit Error */}
            {errors.submit && (
              <div
                className={`bg-red-500/10 border border-red-500/30 rounded-lg ${
                  isWidget ? "p-2" : "p-3"
                }`}
              >
                <p
                  className={`${isWidget ? "text-xs" : "text-sm"} text-red-400`}
                >
                  {errors.submit}
                </p>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting}
              className={`w-full ${
                isWidget ? "py-2.5" : "py-3"
              } px-4 rounded-xl font-medium transition-all duration-300 
                         transform hover:scale-[1.02] active:scale-[0.98] ${
                           isWidget ? "text-sm" : ""
                         }
                         ${
                           isSubmitting
                             ? "bg-gray-700 text-gray-400 cursor-not-allowed"
                             : "bg-gradient-to-r from-blue-500 to-purple-600 text-white hover:shadow-lg hover:shadow-blue-500/30"
                         }`}
            >
              {isSubmitting ? (
                <div className="flex items-center justify-center space-x-2">
                  <svg
                    className={`animate-spin ${
                      isWidget ? "h-4 w-4" : "h-5 w-5"
                    }`}
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                      fill="none"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  <span>Getting Started...</span>
                </div>
              ) : (
                "Start Chatting"
              )}
            </button>
          </form>

          {/* Privacy Note - Only show in non-widget mode */}
          {!isWidget && (
            <p className="text-xs text-gray-500 text-center mt-4">
              By continuing, you agree to our privacy policy and terms of
              service.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserRegistration;
